import React, { useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHome, FaUsers, FaBuilding, FaList, FaTicketAlt, FaUsersCog, FaLayerGroup, FaUserShield, FaDollarSign, FaChartLine, FaUserFriends, FaRobot, FaBrain, FaPuzzlePiece, FaPlug, FaHeadset } from 'react-icons/fa';
import logo from '../../assets/images/logo.png';
import AgentDot from '../../components/ui/AgentDot';
import { useSubNav } from '../../context/SubNavContext';
import { useAuth } from '../../context/AuthContext';

// Grupos por lo que hace el equipo: crear y atender agentes, atender clientes, administrar la plataforma.
const groups = [
  { title: null, items: [
    { path: '/dashboard', icon: FaHome, label: 'Inicio' },
  ] },
  { title: 'Agentes', items: [
    { path: '/agentes', icon: FaBrain, label: 'Agentes' },
    { path: '/herramientas', icon: FaPuzzlePiece, label: 'Herramientas' },
    { path: '/integraciones', icon: FaPlug, label: 'Integraciones' },
    { path: '/proyects', icon: FaBuilding, label: 'Proyectos' },
    { path: '/agent-types', icon: FaRobot, label: 'Tipos de agente' },
  ] },
  { title: 'Clientes', items: [
    { path: '/clients', icon: FaUsers, label: 'Clientes' },
    { path: '/proyect_manager', icon: FaList, label: 'Proyecto Manager' },
    { path: '/support', icon: FaTicketAlt, label: 'Soporte' },
  ] },
  { title: 'Administración', items: [
    { path: '/admin-usuarios', icon: FaUsersCog, label: 'Usuarios' },
    { path: '/admin-cuentas', icon: FaUserShield, label: 'Administradores' },
    { path: '/admin-equipo', icon: FaUserFriends, label: 'Equipo' },
    { path: '/admin-planes', icon: FaLayerGroup, label: 'Planes' },
    { path: '/admin-asistente', icon: FaHeadset, label: 'Uso del asistente' },
    { path: '/admin-costos', icon: FaChartLine, label: 'Consumo y costos' },
    { path: '/admin-precios', icon: FaDollarSign, label: 'Precios de modelos' },
  ] },
];
const menuItems = groups.flatMap((g) => g.items);
// El empleado solo tiene el grupo Agentes (no Clientes ni Administración). Dentro, Proyectos e Integraciones le muestran únicamente lo de su proyecto
// interno y Tipos de agente es de solo lectura: lo exige la base de datos, esto solo evita mostrarle lo que no puede abrir.
const EMPLOYEE_GROUPS = groups.filter((g) => g.title === 'Agentes');

const SideMenu = ({ isOpen, isMobile, toggleSideMenu, setTitle }) => {
  const location = useLocation();
  const sub = useSubNav();
  const { role } = useAuth();
  const shown = role === 'employee' ? EMPLOYEE_GROUPS : groups;
  const section = '/' + location.pathname.replace(/^\//, '').split('/')[0];
  const isActive = (path) => section === path || (path === '/dashboard' && section === '/');

  useEffect(() => {
    const parts = location.pathname.replace(/^\//, '').split('/');
    const last = parts[parts.length - 1];
    let label = menuItems.find((item) => item.path === '/' + parts[0])?.label ?? '';
    if (last === 'add') label += ' · Agregar';
    if (last === 'edit') label += ' · Editar';
    if (last === 'ticket') label += ' · Ticket';
    setTitle(label);
  }, [location.pathname, setTitle]);

  return (
    <>
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={toggleSideMenu} aria-hidden />
        )}
      </AnimatePresence>

      <motion.aside
        aria-label="Menú principal"
        initial={false}
        animate={{ x: isOpen ? 0 : -260 }}
        transition={{ type: 'tween', duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 left-0 h-full w-[248px] z-40 flex flex-col bg-panel border-r border-line"
        inert={!isOpen}
      >
        <div className="flex items-center gap-3 px-5 h-16 shrink-0">
          <img src={logo} alt="Cantera Digital" className="sidebar-logo h-8 object-contain" />
        </div>

        <nav className="flex-1 px-3 pb-4 overflow-y-auto">
          {shown.map((g, gi) => (
            <div key={gi} className={gi ? 'mt-5' : 'mt-1'}>
              {g.title && <p className="px-3 mb-1.5 text-[12px] font-semibold text-fg-faint">{g.title}</p>}
              <ul className="space-y-0.5">
                {g.items.map((item) => {
                  const active = isActive(item.path);
                  const Icon = item.icon;
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={() => isMobile && toggleSideMenu()}
                        aria-current={active ? 'page' : undefined}
                        className={`relative flex items-center gap-3 h-10 px-3 rounded-full text-[14px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70
                          ${active ? 'text-fg' : 'text-fg-dim hover:text-fg hover:bg-raised/70'}`}
                      >
                        {active && <motion.span layoutId="nav-active" transition={{ type: 'spring', stiffness: 420, damping: 34 }} className="absolute inset-0 rounded-full bg-raised ring-1 ring-line" />}
                        <Icon aria-hidden className={`relative h-[15px] w-[15px] ${active ? 'text-fg' : 'text-fg-faint'}`} />
                        <span className="relative">{item.label}</span>
                      </Link>
                      {sub && sub.parent === item.path && <SubNav nav={sub} onPick={() => isMobile && toggleSideMenu()} />}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="px-5 py-4 border-t border-line flex items-center gap-2.5">
          <AgentDot name="Cantera" palette="aurora" size={22} />
          <p className="text-[12px] text-fg-faint leading-tight">Plataforma de agentes<br /><span className="text-fg-dim">Cantera Digital</span></p>
        </div>
      </motion.aside>
    </>
  );
};

// Las secciones de lo que está abierto (p. ej. un agente), colgadas de su entrada del menú.
function SubNav({ nav, onPick }) {
  return (
    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className="overflow-hidden">
      <div className="mt-1 mb-2 ml-[18px] pl-3 border-l border-line">
        <div className="flex items-center gap-2 px-2 py-1.5 text-[12.5px] font-semibold text-fg min-w-0" title={nav.title}>
          {nav.avatar && <AgentDot name={nav.avatar.name} colors={nav.avatar.colors} shape={nav.avatar.shape} imageUrl={nav.avatar.imageUrl} size={18} follow={false} />}
          <span className="truncate">{nav.title}</span>
        </div>
        <ul aria-label={`Secciones de ${nav.title}`} className="space-y-px">
          {nav.items.map((it) => {
            const Icon = it.icon;
            return (
              <li key={it.to}>
                <NavLink to={it.to} end onClick={onPick}
                  className={({ isActive }) => `group flex items-center gap-2.5 h-8 px-2 rounded-lg text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70
                    ${isActive ? 'bg-raised text-fg font-semibold' : 'text-fg-dim hover:text-fg hover:bg-raised/60'}`}>
                  {Icon && <Icon aria-hidden className="h-3 w-3 shrink-0 opacity-70" />}
                  <span className="truncate">{it.label}</span>
                  {it.badge && <span className={`ml-auto h-1.5 w-1.5 shrink-0 rounded-full ${it.badgeTone === 'signal' ? 'bg-signal' : 'bg-caution'}`} title={it.badge} aria-label={it.badge} />}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );
}

export default SideMenu;
